import { useState } from 'react';
import { message, Form } from 'antd';
import { SOP_CONSTANTS } from '../../../constants/sopConstants';
import { generateUniqueStepCode } from '../../../utils/sopUtils';
import dayjs from 'dayjs';

// 辅助函数：解析executeTime，支持多种格式
const parseExecuteTime = (timeStr) => {
    if (!timeStr) return null;
    
    try {
        // 尝试直接解析为dayjs对象
        let time = dayjs(timeStr);
        
        // 如果不是有效时间，尝试不同格式
        if (!time.isValid()) {
            // 检查是否只有时间部分 (HH:mm:ss)
            if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(timeStr)) {
                // 添加日期部分再解析
                time = dayjs(`2000-01-01 ${timeStr}`);
            }
        }
        
        // 如果解析成功，返回dayjs对象
        if (time.isValid()) {
            return time;
        }
        
        console.warn('无法解析executeTime格式:', timeStr);
        return null;
    } catch (error) {
        console.error('解析executeTime出错:', error, timeStr);
        return null;
    }
};

export const useStepManagement = (steps, setSteps, messageList, setMessageList, setPreviewUrls) => {
    const [currentStep, setCurrentStep] = useState(null);
    const [isStepModalVisible, setIsStepModalVisible] = useState(false);
    const [stepForm] = Form.useForm();

    // 保存步骤
    const handleSaveStep = async (stepData) => {
        try {
            // 验证表单
            await stepForm.validateFields();

            // 获取表单数据
            const values = stepForm.getFieldsValue();

            // 处理executeTime - 确保只获取时分秒，不包含日期和时区信息
            let executeTime = null;
            if (values.executeTime) {
                // 确保是dayjs对象并格式化为标准时间格式
                if (dayjs.isDayjs(values.executeTime)) {
                    executeTime = values.executeTime.format('HH:mm:ss');
                } else if (typeof values.executeTime === 'string') {
                    // 如果是字符串，尝试解析并格式化
                    const parsedTime = parseExecuteTime(values.executeTime);
                    if (parsedTime) {
                        executeTime = parsedTime.format('HH:mm:ss');
                    } else {
                        executeTime = values.executeTime;
                    }
                } else {
                    // 其他情况，转为字符串
                    executeTime = String(values.executeTime);
                }
                console.log('处理后的executeTime:', executeTime);
            }

            console.log('保存步骤，接收到的数据:', stepData);

            // 确保朋友圈类型的步骤保持正确的type和actionType
            let stepType = values.type;
            let stepActionType = values.actionType;

            // 如果当前步骤是朋友圈类型或从StepModal返回的数据表明这是朋友圈类型
            if ((currentStep && currentStep.type === 'MOMENTS') ||
                (currentStep && currentStep.actionType === 'SEND_MOMENT') ||
                (stepData && stepData.momentsList && stepData.momentsList.length > 0)) {
                stepType = 'MOMENTS';
                stepActionType = 'SEND_MOMENT';
            }

            // 构建步骤对象（确保结构一致性）
            const stepObj = {
                // 如果是编辑模式，保留ID
                ...(currentStep && currentStep.id ? { id: currentStep.id } : {}),

                // 基本字段信息
                name: values.name,
                description: values.description || '',
                stepCode: values.stepCode || generateUniqueStepCode(),
                type: stepType,
                actionType: stepActionType,
                timeOffsetDays: values.timeOffsetDays || 0,
                executeTime,

                // 直接使用从StepModal返回的数据
                actionParams: stepData.actionParams || {},

                // 标记步骤已被编辑，用于保存判断
                _edited: true,
            };

            // 根据步骤类型处理相关列表
            if (stepType === 'MESSAGE') {
                // 直接使用从StepModal返回的messageList
                if (stepData && stepData.messageList && stepData.messageList.length > 0) {
                    console.log('使用StepModal返回的messageList:', stepData.messageList);
                    stepObj.messageList = [...stepData.messageList];
                }
                // 如果没有消息列表，添加一个空消息
                else {
                    console.warn('StepModal没有返回有效的messageList，创建默认消息');
                    stepObj.messageList = [{
                        id: 'text-' + Date.now() + Math.random(),
                        type: 'TEXT',
                        params: {
                            content: ''
                        }
                    }];

                    // 同时更新actionParams
                    stepObj.actionParams = {
                        subtype: 'MESSAGE',
                        text: '',
                        attachments: []
                    };
                }
            }
            else if (stepType === 'MOMENTS') {
                // 确保使用正确的类型值
                stepObj.type = 'MOMENTS';
                stepObj.actionType = 'SEND_MOMENT';

                // 直接使用从StepModal返回的momentsList
                if (stepData && stepData.momentsList) {
                    console.log('使用StepModal返回的momentsList:', stepData.momentsList);
                    stepObj.momentsList = [...stepData.momentsList];
                } else if (currentStep && currentStep.momentsList) {
                    // 如果StepModal没有返回momentsList但当前步骤有，则保留
                    console.log('保留当前步骤的momentsList:', currentStep.momentsList);
                    stepObj.momentsList = [...currentStep.momentsList];
                } else {
                    console.warn('没有找到有效的momentsList，创建空列表');
                    stepObj.momentsList = [];
                }
            }
            else if (stepType === 'TAG') {
                // 获取原始actionParams数据，如果存在的话
                let originalActionParams = {};
                if (stepData && stepData.actionParams) {
                    try {
                        // 如果是字符串，尝试解析
                        if (typeof stepData.actionParams === 'string') {
                            originalActionParams = JSON.parse(stepData.actionParams);
                        }
                        // 如果是对象，直接使用
                        else if (typeof stepData.actionParams === 'object') {
                            originalActionParams = stepData.actionParams;
                        }
                    } catch (e) {
                        console.error('解析标签步骤actionParams失败:', e);
                    }
                }

                // 标签操作 - 首先尝试使用新设置的数据
                if (stepData && stepData.actionParams && stepData.actionParams.tagId) {
                    stepObj.actionParams = {
                        operation: stepActionType === 'ADD_TAG' ? 'ADD' : 'REMOVE',
                        tagId: stepData.actionParams.tagId
                    };
                }
                // 如果没有新设置的数据，回退到原始数据
                else if (originalActionParams && originalActionParams.tagId) {
                    stepObj.actionParams = {
                        operation: originalActionParams.operation || (stepActionType === 'ADD_TAG' ? 'ADD' : 'REMOVE'),
                        tagId: originalActionParams.tagId
                    };
                }
                // 兜底逻辑
                else {
                    console.warn('标签步骤没有有效的tagId');
                    stepObj.actionParams = {
                        operation: stepActionType === 'ADD_TAG' ? 'ADD' : 'REMOVE',
                        tagId: null
                    };
                }
            }

            console.log('保存步骤，最终步骤对象:', stepObj);

            if (currentStep) {
                // 编辑模式
                const index = steps.findIndex(s =>
                    (s.id && s.id === currentStep.id) ||
                    (s.stepCode && s.stepCode === currentStep.stepCode)
                );

                if (index !== -1) {
                    const newSteps = [...steps];
                    newSteps[index] = stepObj;
                    setSteps(newSteps);
                }
            } else {
                // 添加模式
                setSteps([...steps, stepObj]);
            }

            // 关闭模态框
            setIsStepModalVisible(false);
            message.success(`步骤${currentStep ? '更新' : '添加'}成功`);
        } catch (error) {
            console.error('表单验证失败:', error);
            message.error('请填写必填字段');
        }
    };

    // 打开添加步骤模态框
    const handleAddStep = () => {
        setCurrentStep(null);
        setMessageList([]);

        // 重置步骤表单
        stepForm.resetFields();

        // 设置默认值
        stepForm.setFieldsValue({
            name: `步骤 ${steps.length + 1}`,
            stepCode: generateUniqueStepCode(),
            type: SOP_CONSTANTS.StepType.MESSAGE,
            actionType: SOP_CONSTANTS.ActionType.TEXT,
            timeOffsetDays: 0,
            executeTime: null,
            actionParams: {
                content: ''
            }
        });

        setIsStepModalVisible(true);
    };

    // 打开编辑步骤模态框
    const handleEditStep = (step, index) => {
        // 创建一个新的步骤对象，避免直接修改原始对象
        const processedStep = { ...step };

        // 记录原始executeTime格式，便于调试
        if (processedStep.executeTime) {
            console.log('原始executeTime:', processedStep.executeTime, '类型:', typeof processedStep.executeTime);
        }

        // 确保保留原始actionParams字符串
        if (typeof step.actionParams === 'object' && step.originalActionParams) {
            processedStep.originalActionParams = step.originalActionParams;
        }

        setCurrentStep(processedStep);
        console.log('编辑步骤:', processedStep);

        // 确保 actionParams 是对象形式
        if (typeof processedStep.actionParams === 'string') {
            try {
                processedStep.actionParams = JSON.parse(processedStep.actionParams);
            } catch (e) {
                console.warn(`步骤 ${processedStep.id || index} actionParams无法解析:`, e);
                processedStep.actionParams = {};
            }
        } else if (!processedStep.actionParams) {
            processedStep.actionParams = {};
        }

        // 处理消息类型步骤
        if (processedStep.type === 'MESSAGE') {
            // 确保有messageList字段
            let newMessageList = processedStep.messageList || [];

            // 如果没有消息列表，但有actionParams，则从actionParams构建
            if (newMessageList.length === 0 && processedStep.actionParams) {
                // 处理文本内容
                if (processedStep.actionParams.text) {
                    newMessageList.push({
                        id: 'text-' + Date.now() + Math.random(),
                        type: 'TEXT',
                        params: {
                            content: processedStep.actionParams.text
                        }
                    });
                }

                // 处理附件列表
                if (processedStep.actionParams.attachments && Array.isArray(processedStep.actionParams.attachments)) {
                    processedStep.actionParams.attachments.forEach(attachment => {
                        if (attachment.msgtype === 'image' && attachment.image) {
                            // 获取mediaId和ossUrl
                            const mediaId = attachment.image.media_id || attachment.image.pic_url;
                            const ossUrl = attachment.image.oss_url || attachment.image.ossUrl;

                            // 添加到messageList
                            newMessageList.push({
                                id: 'image-' + Date.now() + Math.random(),
                                type: 'IMAGE',
                                params: {
                                    mediaId: mediaId,
                                    mediaId_ossUrl: ossUrl // 添加ossUrl
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && mediaId) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [mediaId]: ossUrl
                                }));
                            }
                        } else if (attachment.msgtype === 'link' && attachment.link) {
                            // 获取picUrl及其对应的ossUrl
                            const picUrl = attachment.link.picurl || '';
                            const ossUrl = attachment.link.oss_url || attachment.link.ossUrl;

                            newMessageList.push({
                                id: 'link-' + Date.now() + Math.random(),
                                type: 'LINK',
                                params: {
                                    title: attachment.link.title || '',
                                    description: attachment.link.desc || '',
                                    url: attachment.link.url || '',
                                    picUrl: picUrl,
                                    picUrl_ossUrl: ossUrl // 添加封面图的ossUrl
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && picUrl) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [picUrl]: ossUrl
                                }));
                            }
                        } else if (attachment.msgtype === 'miniprogram' && attachment.miniprogram) {
                            // 获取thumbMediaId及其对应的ossUrl
                            const thumbMediaId = attachment.miniprogram.pic_media_id || '';
                            const ossUrl = attachment.miniprogram.oss_url || attachment.miniprogram.ossUrl;

                            newMessageList.push({
                                id: 'miniprogram-' + Date.now() + Math.random(),
                                type: 'MINIPROGRAM',
                                params: {
                                    title: attachment.miniprogram.title || '',
                                    appid: attachment.miniprogram.appid || '',
                                    pagepath: attachment.miniprogram.page || '',
                                    thumbMediaId: thumbMediaId,
                                    thumbMediaId_ossUrl: ossUrl // 添加封面图的ossUrl
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && thumbMediaId) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [thumbMediaId]: ossUrl
                                }));
                            }
                        } else if (attachment.msgtype === 'video' && attachment.video) {
                            // 获取mediaId和ossUrl
                            const mediaId = attachment.video.media_id || '';
                            const ossUrl = attachment.video.oss_url || attachment.video.ossUrl;

                            newMessageList.push({
                                id: 'video-' + Date.now() + Math.random(),
                                type: 'VIDEO',
                                params: {
                                    mediaId: mediaId,
                                    ossUrl: ossUrl // 使用统一的ossUrl字段名
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && mediaId) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [mediaId]: ossUrl
                                }));
                            }
                        } else if (attachment.msgtype === 'file' && attachment.file) {
                            // 获取mediaId和ossUrl
                            const mediaId = attachment.file.media_id || '';
                            const ossUrl = attachment.file.oss_url || attachment.file.ossUrl;

                            newMessageList.push({
                                id: 'file-' + Date.now() + Math.random(),
                                type: 'FILE',
                                params: {
                                    mediaId: mediaId,
                                    mediaId_ossUrl: ossUrl // 添加ossUrl
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && mediaId) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [mediaId]: ossUrl
                                }));
                            }
                        }
                    });
                }
            }

            // 确保当前步骤也保存了最新的messageList
            processedStep.messageList = newMessageList;

            // 设置消息列表到状态中
            setMessageList(newMessageList);
        }
        // 处理朋友圈类型步骤
        else if (processedStep.type === 'MOMENTS' || processedStep.actionType === 'SEND_MOMENT') {
            // 清空消息列表，避免干扰
            setMessageList([]);

            // 确保设置正确的type
            processedStep.type = 'MOMENTS';
            processedStep.actionType = 'SEND_MOMENT';

            // 初始化momentsList
            if (!processedStep.momentsList || processedStep.momentsList.length === 0) {
                processedStep.momentsList = [];

                // 从actionParams解析朋友圈内容
                let actionParams = processedStep.actionParams;

                // 处理文本内容
                if (actionParams.text && actionParams.text.content) {
                    processedStep.momentsList.push({
                        id: 'moment-text-' + Date.now() + Math.random(),
                        type: 'MOMENT_TEXT',
                        params: {
                            content: actionParams.text.content
                        }
                    });
                }

                // 处理附件内容
                if (actionParams.attachments && Array.isArray(actionParams.attachments)) {
                    actionParams.attachments.forEach(attachment => {
                        if (attachment.msgtype === 'image' && attachment.image) {
                            // 获取mediaId和ossUrl
                            const mediaId = attachment.image.media_id || attachment.image.pic_url;
                            const ossUrl = attachment.image.oss_url || attachment.image.ossUrl;

                            processedStep.momentsList.push({
                                id: 'moment-image-' + Date.now() + Math.random(),
                                type: 'MOMENT_IMAGE',
                                params: {
                                    mediaId: mediaId,
                                    mediaId_ossUrl: ossUrl // 添加ossUrl
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && mediaId) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [mediaId]: ossUrl
                                }));
                            }
                        } else if (attachment.msgtype === 'video' && attachment.video) {
                            // 获取mediaId和ossUrl
                            const mediaId = attachment.video.media_id || '';
                            const ossUrl = attachment.video.oss_url || attachment.video.ossUrl;

                            processedStep.momentsList.push({
                                id: 'moment-video-' + Date.now() + Math.random(),
                                type: 'MOMENT_VIDEO',
                                params: {
                                    mediaId: mediaId,
                                    ossUrl: ossUrl // 使用统一的ossUrl字段名
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && mediaId) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [mediaId]: ossUrl
                                }));
                            }
                        } else if (attachment.msgtype === 'link' && attachment.link) {
                            // 获取picUrl和ossUrl
                            const picUrl = attachment.link.picurl || '';
                            const ossUrl = attachment.link.oss_url || attachment.link.ossUrl;

                            processedStep.momentsList.push({
                                id: 'moment-link-' + Date.now() + Math.random(),
                                type: 'MOMENT_LINK',
                                params: {
                                    title: attachment.link.title || '',
                                    description: attachment.link.desc || '',
                                    url: attachment.link.url || '',
                                    picUrl: picUrl,
                                    picUrl_ossUrl: ossUrl // 添加ossUrl
                                }
                            });

                            // 如果ossUrl存在，更新预览URL
                            if (ossUrl && picUrl) {
                                setPreviewUrls(prev => ({
                                    ...prev,
                                    [picUrl]: ossUrl
                                }));
                            }
                        }
                    });
                }
            }
        }
        // 其他类型步骤 (如TAG类型)
        else {
            // 清空消息列表
            setMessageList([]);
        }

        // 重置表单
        stepForm.resetFields();

        // 初始值转换
        let initialValues = {
            ...processedStep,
            executeTime: processedStep.executeTime ? parseExecuteTime(processedStep.executeTime) : null
        };

        // 为MESSAGE类型的TEXT子类型特别处理content字段
        if (processedStep.type === 'MESSAGE' && processedStep.actionType === 'TEXT') {
            // 确保content字段有值
            initialValues.actionParams = {
                ...(initialValues.actionParams || {}),
                content: initialValues.actionParams.content || initialValues.actionParams.text || ''
            };
        }

        // 为MOMENTS类型特别处理，确保actionParams中有content字段
        if (processedStep.type === 'MOMENTS') {
            const textItem = processedStep.momentsList && processedStep.momentsList.find(item => item.type === 'MOMENT_TEXT');
            if (textItem && textItem.params && textItem.params.content) {
                initialValues.actionParams = {
                    ...(initialValues.actionParams || {}),
                    content: textItem.params.content
                };
            }
        }

        // 设置表单值
        stepForm.setFieldsValue(initialValues);

        setIsStepModalVisible(true);
    };

    // 复制步骤
    const handleCopyStep = (indexOrStep) => {
        // 判断参数类型
        let step;
        
        if (typeof indexOrStep === 'number') {
            // 如果是数字，视为索引
            step = steps[indexOrStep];
        } else if (typeof indexOrStep === 'object' && indexOrStep !== null) {
            // 如果是对象，直接使用
            step = indexOrStep;
        } else {
            console.error('无效的参数类型:', indexOrStep);
            message.error('复制步骤失败：无效的参数');
            return;
        }
        
        // 安全检查，确保有有效的step对象
        if (!step) {
            console.error('找不到要复制的步骤:', indexOrStep);
            message.error('复制步骤失败：找不到要复制的步骤');
            return;
        }

        // 创建深拷贝，确保复制所有属性
        const newStep = {
            ...step,
            id: undefined, // 移除ID，以便创建新记录
            stepCode: generateUniqueStepCode(),
            name: `${step.name || '步骤'} (复制)`, // 添加默认名称，防止undefined
            _edited: true,
        };

        // 确保messageList和momentsList被深度复制
        if (step.messageList && step.messageList.length > 0) {
            newStep.messageList = JSON.parse(JSON.stringify(step.messageList));
        }
        
        if (step.momentsList && step.momentsList.length > 0) {
            newStep.momentsList = JSON.parse(JSON.stringify(step.momentsList));
        }

        // 添加到步骤列表
        setSteps([...steps, newStep]);

        message.success('步骤复制成功');
    };

    // 删除步骤
    const handleDeleteStep = (index) => {
        const newSteps = [...steps];
        newSteps.splice(index, 1);
        setSteps(newSteps);
        message.success('步骤删除成功');
    };

    return {
        steps,
        setSteps,
        currentStep,
        setCurrentStep,
        isStepModalVisible,
        setIsStepModalVisible,
        stepForm,
        handleSaveStep,
        handleAddStep,
        handleEditStep,
        handleCopyStep,
        handleDeleteStep
    };
}